import React, { useState } from 'react';
import * as yup from 'yup';
import { Formik } from 'formik';
import { Link } from 'react-router';
import axios from 'axios';
import LabelledInput from '@ordermentum/ui-shared/molecules/LabelledInput';
import { Button } from '@ordermentum/ui-shared/atoms/Button';
import Spinner from '@ordermentum/ui-shared/atoms/Spinner';
import Banner from '@ordermentum/ui-shared/atoms/Banner';
import { useFlags } from '@ordermentum/ui-shared/hooks/useFlags';
import { safeRedirect } from '@ordermentum/ui-shared/helpers/urls';
import { ProviderLogin } from '../ProviderLogin';
import { SignInContainer } from '../LoginBase.styles';
import { history } from '../../../lib/history';
import {
  SignInText,
  SignInLabelContainer,
  FieldWrapper,
  ForgottenPassword,
  FormWrapper,
  ErrorBannerWrapper,
  SignInSwitch,
  AuthActionContainer,
} from './index.styles';
import { LoginFormProps } from './index.types';
import { MessageBanner } from './MessageBanner';
import { MFAAction } from './MFAForm';

export const SubmissionButton = ({
  loading,
  children,
}: {
  loading: boolean;
  children: React.ReactNode;
}) => (
  <Button
    name="submit-button"
    type="primary-submit"
    borderRadius="6"
    size="max"
    height="normal"
  >
    {loading ? <Spinner size="md" color="white" /> : children}
  </Button>
);

export const SignInForm = ({
  onSignUpClick,
  onPasswordResetClick,
  onSubmitSuccess,
  location,
  messageBannerState,
  destination,
}: LoginFormProps) => {
  const [reqError, setReqError] = useState<string | undefined>();
  const flags = useFlags(['google-login', 'xero-login']);

  const SignupLink = ({ children }: React.PropsWithChildren<{}>) =>
    onSignUpClick ? (
      <Button type="reset" onClick={onSignUpClick}>
        {children}
      </Button>
    ) : (
      <Link to="/sign-up">{children}</Link>
    );

  const PasswordResetLink = ({ children }: React.PropsWithChildren<{}>) =>
    onPasswordResetClick ? (
      <Button type="reset" onClick={onPasswordResetClick}>
        {children}
      </Button>
    ) : (
      <Link to="password-reset">{children}</Link>
    );

  const submit = async values => {
    setReqError(undefined);
    try {
      await axios.post('/v1/auth', {
        username: values.email,
        password: values.password,
      });
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err) {
      if (err.response.status === 403) {
        if (err.response.data.error === 'mfa_required') {
          const { method, state, target, userId } = err.response.data.data;
          history.push(
            `/mfa?method=${method}&state=${state}&target=${target}&userId=${userId}&action=${MFAAction.LOGIN}&destination=${destination}`
          );
          return;
        }
        setReqError('Invalid email or password');
        return;
      }
      if (err.response.status) {
        setReqError(err.response.data.message);
        return;
      }
      setReqError('Something unexpected occurred, please try again later');
      return;
    }
    if (destination) {
      window.location.href = safeRedirect(destination);
    } else {
      window.location.reload();
    }
  };

  return (
    <SignInContainer>
      {messageBannerState && <MessageBanner type={messageBannerState} />}
      <SignInLabelContainer>
        <SignInText>Sign in</SignInText>
        <SignInSwitch data-testid="signup-link">
          or <SignupLink>create an account</SignupLink>
        </SignInSwitch>
      </SignInLabelContainer>
      {reqError && (
        <ErrorBannerWrapper>
          <Banner type="warning" noIcon dismiss={false}>
            {reqError}
          </Banner>
        </ErrorBannerWrapper>
      )}
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={() =>
          yup.object().shape({
            email: yup.string().required(),
            password: yup.string().required(),
          })
        }
        onSubmit={submit}
      >
        {formik => {
          const isLoading = formik.isSubmitting;
          return (
            <form onSubmit={formik.handleSubmit}>
              <FormWrapper>
                <FieldWrapper>
                  <LabelledInput
                    label="Email address"
                    required
                    tag="email"
                    value={formik.values.email}
                    onChange={formik.setFieldValue}
                    size="max"
                  />
                </FieldWrapper>
                <FieldWrapper>
                  <LabelledInput
                    label="Password"
                    required
                    tag="password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.setFieldValue}
                    size="max"
                  />
                </FieldWrapper>
                <AuthActionContainer data-testid="signin-button">
                  <SubmissionButton loading={isLoading}>
                    Sign in
                  </SubmissionButton>
                  <ForgottenPassword data-testid="forgot-password-link">
                    <PasswordResetLink>Forgot your password?</PasswordResetLink>
                  </ForgottenPassword>
                  {<ProviderLogin location={location} flags={flags ?? {}} />}
                </AuthActionContainer>
              </FormWrapper>
            </form>
          );
        }}
      </Formik>
    </SignInContainer>
  );
};
